import PageShell from "@/components/PageShell";
import usePageMeta from "@/lib/usePageMeta";

export const Privacy = () => {
  usePageMeta({
    title: "Privacy Policy — Postmark & Co.",
    description:
      "How Postmark & Co. collects, uses, and protects your information. Covers cookies, analytics (Google Analytics, PostHog), advertising (Google AdSense), affiliate tracking, and your rights under GDPR and CCPA.",
    canonical: "https://maildirect.store/privacy",
  });

  return (
    <PageShell testid="privacy-page">
      <article className="max-w-3xl mx-auto px-6 md:px-12 prose-editorial">
        <div className="postmark text-[#D93829] mb-6">
          <span className="w-1 h-1 rounded-full bg-[#D93829]" />
          Legal
        </div>
        <h1 className="font-serif-display text-5xl md:text-6xl leading-[1.05] tracking-tight text-[#1C1C1A]">
          Privacy Policy
        </h1>
        <p className="text-[11px] uppercase tracking-[0.24em] text-[#68655E] mt-4">
          Last updated: December 2025
        </p>

        <div className="mt-12 space-y-6 text-[#3B3A36] text-base md:text-lg leading-relaxed font-sans-body">
          <p>
            Postmark &amp; Co. (&ldquo;we,&rdquo; &ldquo;our,&rdquo; &ldquo;us&rdquo;)
            operates the website at maildirect.store. This Privacy Policy
            explains what information we collect, how we use it, and the
            choices available to you.
          </p>

          <h2 className="font-serif-display text-2xl text-[#1C1C1A] pt-4">
            1. Information we collect
          </h2>
          <p>
            <strong>Automatically collected:</strong> IP address, browser type,
            device type, referring URL, pages visited, and time spent on the
            site. This is collected via Google Analytics 4 and PostHog for
            audience analytics and product improvement.
          </p>
          <p>
            <strong>Cookies:</strong> We use first-party and third-party
            cookies to remember preferences, measure traffic, and (through
            Google AdSense) show relevant advertising. You can disable cookies
            in your browser at any time.
          </p>
          <p>
            <strong>Voluntarily provided:</strong> If you contact us via email,
            we retain the email address and message content for as long as
            necessary to respond and comply with legal obligations.
          </p>

          <h2 className="font-serif-display text-2xl text-[#1C1C1A] pt-4">
            2. Third-party services
          </h2>
          <ul className="list-disc pl-6 space-y-2">
            <li>
              <strong>Google Analytics 4</strong> — anonymized usage measurement.{" "}
              <a
                href="https://policies.google.com/privacy"
                target="_blank"
                rel="noopener noreferrer"
                className="text-[#D93829] link-underline"
              >
                Google privacy policy
              </a>
              .
            </li>
            <li>
              <strong>Google AdSense</strong> — third-party vendor uses cookies
              to serve ads based on prior visits to this and other websites.
              Google&rsquo;s use of advertising cookies enables it and its
              partners to serve ads based on your visit. You may opt out of
              personalized advertising at{" "}
              <a
                href="https://www.google.com/settings/ads"
                target="_blank"
                rel="noopener noreferrer"
                className="text-[#D93829] link-underline"
              >
                google.com/settings/ads
              </a>
              .
            </li>
            <li>
              <strong>PostHog</strong> — product analytics and session
              understanding.
            </li>
            <li>
              <strong>Thanks.io (affiliate partner)</strong> — when you click
              an outbound link to Thanks.io, they may set their own cookies to
              track affiliate attribution.
            </li>
          </ul>

          <h2 className="font-serif-display text-2xl text-[#1C1C1A] pt-4">
            3. How we use information
          </h2>
          <ul className="list-disc pl-6 space-y-2">
            <li>Measure which pages and sections engage readers.</li>
            <li>Improve editorial content and navigation.</li>
            <li>Serve relevant ads via Google AdSense.</li>
            <li>Attribute affiliate referrals to Thanks.io.</li>
            <li>Prevent abuse, spam, and fraudulent activity.</li>
          </ul>

          <h2 className="font-serif-display text-2xl text-[#1C1C1A] pt-4">
            4. Your rights (GDPR / CCPA)
          </h2>
          <p>
            If you are in the European Economic Area, United Kingdom, or
            California, you have the right to access, correct, delete, or
            export the personal data we hold about you, and to opt out of the
            &ldquo;sale&rdquo; of personal information (Postmark &amp; Co. does
            not sell personal information). To exercise any of these rights,
            email us via the{" "}
            <a
              href="/contact"
              className="text-[#D93829] link-underline"
              data-testid="privacy-contact-link"
            >
              contact page
            </a>
            .
          </p>

          <h2 className="font-serif-display text-2xl text-[#1C1C1A] pt-4">
            5. Cookie consent
          </h2>
          <p>
            When you first visit this site, a banner will offer you the choice
            to accept or reject non-essential cookies. Your choice is stored
            in local storage and respected across future visits. You can
            change your choice at any time by clearing your browser storage
            for this site.
          </p>

          <h2 className="font-serif-display text-2xl text-[#1C1C1A] pt-4">
            6. Children&rsquo;s privacy
          </h2>
          <p>
            This site is not directed at children under 13 and we do not
            knowingly collect information from them.
          </p>

          <h2 className="font-serif-display text-2xl text-[#1C1C1A] pt-4">
            7. Changes to this policy
          </h2>
          <p>
            We may update this Privacy Policy periodically. The
            &ldquo;last updated&rdquo; date at the top of this page will
            reflect the most recent revision.
          </p>

          <h2 className="font-serif-display text-2xl text-[#1C1C1A] pt-4">
            8. Contact
          </h2>
          <p>
            Questions about this policy?{" "}
            <a
              href="/contact"
              className="text-[#D93829] link-underline"
              data-testid="privacy-contact-link-2"
            >
              Reach us here
            </a>
            .
          </p>
        </div>
      </article>
    </PageShell>
  );
};

export default Privacy;
